import React, {useEffect, useRef, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import Layout from '../components/Layout';
import {api} from '../../api';

function SearchPicker({label, value, displayValue, onSelect, onClear, searchFn}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const timer = setTimeout(() => searchFn(query).then(setResults), 200);
    return () => clearTimeout(timer);
  }, [query, searchFn]);

  if (value) {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <div className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50">
          <span className="flex-1 text-gray-900">{displayValue}</span>
          <button type="button" onClick={() => { onClear(); setQuery(''); }} className="text-gray-400 hover:text-red-500 text-sm">✕</button>
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input type="text" value={query} placeholder={`Search ${label.toLowerCase()}...`}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { if (query.trim()) setOpen(true); }}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 outline-none" />
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {results.map(r => (
            <button key={r.id} type="button" onClick={() => { onSelect(r); setQuery(''); setOpen(false); }}
              className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm">{r.displayName}</button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function EditEstimate() {
  const {id} = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [lineItems, setLineItems] = useState([]);

  useEffect(() => {
    api(`/estimates/${id}`).then(r => r.ok ? r.json() : null).then(est => {
      if (!est) return;
      setForm({
        title: est.title || '', companyId: est.companyId || '', companyName: est.companyName || '',
        contactId: est.contactId || '', contactName: est.contactName || '',
        dealId: est.dealId || '',
        notes: est.notes || '', validUntil: est.validUntil || '',
      });
      setLineItems(est.lineItems?.length ? est.lineItems.map(li => ({description: li.description, quantity: li.quantity, unitPrice: li.unitPrice})) : [{description: '', quantity: 1, unitPrice: 0}]);
    });
  }, [id]);

  const searchCompanies = async (q) => {
    const res = await api('/companies');
    if (!res.ok) return [];
    const all = await res.json();
    return all.filter(c => c.name?.toLowerCase().includes(q.toLowerCase())).slice(0, 10)
      .map(c => ({id: c.id, displayName: c.name}));
  };

  const searchContacts = async (q) => {
    const res = await api('/contacts');
    if (!res.ok) return [];
    const all = await res.json();
    const lower = q.toLowerCase();
    return all.filter(c => `${c.firstName} ${c.lastName}`.toLowerCase().includes(lower)).slice(0, 10)
      .map(c => ({id: c.id, displayName: `${c.firstName || ''} ${c.lastName || ''}`.trim() + (c.companies?.[0]?.name ? ` — ${c.companies[0].name}` : '')}));
  };

  const updateItem = (i, field, value) => {
    const items = [...lineItems];
    items[i] = {...items[i], [field]: value};
    setLineItems(items);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const body = {
      title: form.title,
      dealId: form.dealId || undefined,
      companyId: form.companyId || undefined,
      contactId: form.contactId || undefined,
      notes: form.notes || undefined,
      validUntil: form.validUntil || undefined,
      lineItems: lineItems.filter(li => li.description.trim()).map((li, i) => ({
        ...li, quantity: Number(li.quantity), unitPrice: Number(li.unitPrice), sortOrder: i,
      })),
    };
    const res = await api(`/estimates/${id}`, {method: 'PUT', body: JSON.stringify(body)});
    if (res.ok) navigate(`/estimates/${id}`);
  };

  if (!form) return <Layout><div className="text-center py-12 text-gray-500">Loading...</div></Layout>;

  const total = lineItems.reduce((sum, li) => sum + (Number(li.quantity) || 0) * (Number(li.unitPrice) || 0), 0);

  return (
    <Layout>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Edit Estimate</h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input type="text" required value={form.title} onChange={e => setForm({...form, title: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 outline-none" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SearchPicker label="Company" value={form.companyId} displayValue={form.companyName}
              onSelect={c => setForm({...form, companyId: c.id, companyName: c.displayName})}
              onClear={() => setForm({...form, companyId: '', companyName: ''})}
              searchFn={searchCompanies} />
            <SearchPicker label="Contact" value={form.contactId} displayValue={form.contactName}
              onSelect={c => setForm({...form, contactId: c.id, contactName: c.displayName})}
              onClear={() => setForm({...form, contactId: '', contactName: ''})}
              searchFn={searchContacts} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Valid Until</label>
            <input type="date" value={form.validUntil} onChange={e => setForm({...form, validUntil: e.target.value})}
              className="w-full md:w-48 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 outline-none" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Line Items</h3>
          <div className="flex gap-3 items-end mb-2 text-xs font-medium text-gray-500 uppercase px-1">
            <div className="flex-1">Description</div>
            <div className="w-20 text-center">Qty</div>
            <div className="w-28 text-center">Unit Price</div>
            <div className="w-24 text-right">Amount</div>
            {lineItems.length > 1 && <div className="w-5"></div>}
          </div>
          <div className="space-y-3">
            {lineItems.map((li, i) => (
              <div key={i} className="flex gap-3 items-start">
                <input type="text" placeholder="Description" value={li.description} onChange={e => updateItem(i, 'description', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 outline-none text-sm" />
                <input type="number" placeholder="Qty" value={li.quantity} onChange={e => updateItem(i, 'quantity', e.target.value)} min="0" step="any"
                  className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 outline-none text-sm" />
                <input type="number" placeholder="Price" value={li.unitPrice} onChange={e => updateItem(i, 'unitPrice', e.target.value)} min="0" step="0.01"
                  className="w-28 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 outline-none text-sm" />
                <div className="w-24 py-2 text-sm text-gray-700 text-right font-medium">
                  ${((Number(li.quantity) || 0) * (Number(li.unitPrice) || 0)).toFixed(2)}
                </div>
                {lineItems.length > 1 && (
                  <button type="button" onClick={() => setLineItems(lineItems.filter((_, j) => j !== i))}
                    className="text-red-400 hover:text-red-600 py-2">✕</button>
                )}
              </div>
            ))}
          </div>
          <button type="button" onClick={() => setLineItems([...lineItems, {description: '', quantity: 1, unitPrice: 0}])}
            className="mt-3 text-sm text-yellow-600 hover:text-yellow-800">+ Add Line Item</button>
          <div className="mt-4 pt-4 border-t flex justify-end">
            <div className="text-lg font-bold text-gray-900">Total: ${total.toFixed(2)}</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 outline-none" />
        </div>

        <div className="flex gap-3">
          <button type="submit" className="px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700">Save Changes</button>
          <button type="button" onClick={() => navigate(`/estimates/${id}`)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
        </div>
      </form>
    </Layout>
  );
}
